<template>
  <div class="login-container">
    <el-card class="login-card">

      <!-- Waiting for the recovery session from the email link -->
      <div v-if="state === 'loading'" class="reset-state">
        <p class="reset-hint">Verifying your reset link&hellip;</p>
      </div>

      <!-- Link is invalid or expired -->
      <div v-else-if="state === 'invalid'" class="reset-state">
        <h2>Link expired</h2>
        <p class="reset-hint">
          This password reset link is invalid or has already been used.
          Please request a new one from the
          <router-link :to="postResetRoute">login page</router-link>.
        </p>
      </div>

      <!-- Recovery session active – let the user choose a new password -->
      <div v-else-if="state === 'ready'">
        <h2>Set new password</h2>

        <el-form @submit.prevent="handleSubmit" label-position="top">
          <el-form-item label="New password">
            <el-input
              v-model="password"
              type="password"
              placeholder="At least 6 characters"
              show-password
              autocomplete="new-password"
            />
          </el-form-item>
          <el-form-item label="Confirm password">
            <el-input
              v-model="confirm"
              type="password"
              placeholder="Repeat new password"
              show-password
              autocomplete="new-password"
            />
          </el-form-item>
          <el-form-item>
            <button type="submit" :disabled="loading" class="submit-btn">
              {{ loading ? 'Saving…' : 'Save new password' }}
            </button>
          </el-form-item>
        </el-form>

        <el-alert
          v-if="errorMessage"
          :title="errorMessage"
          type="error"
          show-icon
          :closable="false"
          class="reset-alert"
        />
      </div>

      <!-- Password updated successfully -->
      <div v-else-if="state === 'done'" class="reset-state">
        <h2>Password updated</h2>
        <p class="reset-hint">Your password has been changed. Redirecting to login&hellip;</p>
      </div>

    </el-card>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { supabase } from '@/supabase.js'

const router = useRouter()
const route = useRoute()

const postResetRoute = route.query.next === 'game-login' ? '/game-login' : '/manage-cards'

// state machine: 'loading' | 'invalid' | 'ready' | 'done'
const state = ref('loading')
const password = ref('')
const confirm = ref('')
const loading = ref(false)
const errorMessage = ref('')

let unsubscribe = null

onMounted(() => {
  const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
    if (event === 'PASSWORD_RECOVERY') {
      state.value = 'ready'
    }
  })
  unsubscribe = subscription

  // Fallback: if there's already an active recovery session in the URL hash
  // (some browsers fire the event before Vue mounts; re-check after a tick)
  setTimeout(async () => {
    if (state.value === 'loading') {
      const { data: { session } } = await supabase.auth.getSession()
      if (session) {
        state.value = 'ready'
      } else {
        state.value = 'invalid'
      }
    }
  }, 3000)
})

onUnmounted(() => {
  unsubscribe?.unsubscribe()
})

const handleSubmit = async () => {
  errorMessage.value = ''

  if (!password.value || password.value.length < 6) {
    errorMessage.value = 'Password must be at least 6 characters.'
    return
  }
  if (password.value !== confirm.value) {
    errorMessage.value = 'Passwords do not match.'
    return
  }

  loading.value = true
  try {
    const { error } = await supabase.auth.updateUser({ password: password.value })
    if (error) throw error

    state.value = 'done'
    await supabase.auth.signOut()
    setTimeout(() => router.push(postResetRoute), 2000)
  } catch (err) {
    errorMessage.value = err.message || 'Could not update password. Please try again.'
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.login-container {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 2rem;
}

.login-card {
  width: 100%;
  max-width: 420px;
}

h2 {
  text-align: center;
  margin: 0 0 1.5rem;
  font-size: 1.4rem;
}

.reset-state {
  text-align: center;
  padding: 1rem 0;
}

.reset-hint {
  color: #666;
  font-size: 0.9rem;
  line-height: 1.5;
  margin-top: 0.5rem;
}

.submit-btn {
  width: 100%;
  padding: 0.65rem 1rem;
  font-size: 0.95rem;
  cursor: pointer;
}

.submit-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.reset-alert {
  margin-top: 1rem;
}
</style>
